import React, { useEffect, useState } from "react";
import { DataTable, TableColumnDef } from "../components/DataTable";
import { TableThumbnail } from "../components/TableThumbnail";
import IconButton from "@mui/material/IconButton";
import { ApiDelete, ApiGet } from "../helpers/API/ApiData";
import moment from "moment";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Tooltip, Box, Paper, CircularProgress, Button } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";

import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { usePermissions } from "../hooks/usePermissions";
import { PageHeader } from "../components/PageHeader";

const Improvements = () => {
  const { canDelete } = usePermissions("/improvements");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [countPerPage, setCountPerPage] = useState(10);
  const [data, setData] = useState<any>();
  const [loadingData, setLoadingData] = useState(false);
  const [search, setSearch] = useState("");
  const [show, setShow] = useState(false);
  const [deleteId, setDeleteId] = useState<string>("");
  const [viewDescription, setViewDescription] = useState<string | null>(null);

  useEffect(() => {
    getAllImprovements();
  }, [page, countPerPage, search]);

  const getAllImprovements = async () => {
    setLoadingData(true);
    const params = `page=${page}&limit=${countPerPage}${search ? `&letter=${search}` : ""}`;
    await ApiGet(`service/find-improvements?${params}`)
      .then((res: any) => {
        setLoadingData(false);
        setData(res?.data?.data ?? []);
        setCount(res?.data?.count ?? 0);
      })
      .catch((error: any) => {
        setLoadingData(false);
        toast.error(error?.response?.data?.message || "Etwas ist schief gelaufen. Bitte versuche es erneut");
      });
  };

  const handleDelete = async () => {
    await ApiDelete(`service/delete-improvements?id=${deleteId}`)
      .then((res: any) => {
        if (res?.status === 200) {
          toast.success("Verbesserung erfolgreich gelöscht");
          setShow(false);
          setPage(1);
          getAllImprovements();
        } else {
          toast.error(res?.data?.message || "Etwas ist schief gelaufen. Bitte versuche es erneut");
        }
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen. Bitte versuche es erneut");
      });
  };

  const columns: TableColumnDef[] = [
    {
      header: "Bild",
      width: "10%",
      render: (row: any) => <TableThumbnail src={row?.image} />,
    },
    {
      header: "Benutzer",
      render: (row: any) =>
        row?.userId
          ? `${row.userId.firstName || ""} ${row.userId.lastName || ""}`.trim() || row.userId.emailId || "-"
          : "-",
      getValue: (row: any) => row?.userId?.firstName || row?.userId?.emailId,
    },
    {
      header: "Beschreibung",
      render: (row: any) => {
        const text = row?.description || "";
        if (!text) return <span>-</span>;
        const isLong = text.length > 150;
        return (
          <span
            onClick={isLong ? () => setViewDescription(text) : undefined}
            style={{
              wordBreak: "break-word",
              cursor: isLong ? "pointer" : "default",
              textDecoration: isLong ? "underline dotted" : "none",
            }}
            title={isLong ? "Vollständigen Text anzeigen" : undefined}
          >
            {isLong ? text.substring(0, 150) + "…" : text}
          </span>
        );
      },
    },
    {
      header: "Sprache",
      render: (row: any) => row?.language || "-",
      getValue: (row: any) => row?.language,
    },
    {
      header: "Erstellt am",
      render: (row: any) => moment(row?.createdAt).format("DD.MM.YYYY"),
      getValue: (row: any) => row?.createdAt,
    },
    {
      header: "Einstellungen",
      render: (row: any) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {canDelete && (
            <Tooltip title="Löschen" arrow>
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setDeleteId(row?._id);
                  setShow(true);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <ToastContainer />
      <Paper sx={{ p: 1 }}>
        <Box sx={{ p: 2, mb: 2 }}>
          <PageHeader title="Verbesserungen" onSearch={(val) => { setSearch(val); setPage(1); }} />

          {loadingData ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataTable
              rows={data || []}
              columns={columns}
              page={page}
              count={count}
              rowsPerPage={countPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setCountPerPage}
            />
          )}
        </Box>
      </Paper>
      <Dialog open={show} onClose={() => setShow(false)}>
        <DialogTitle sx={{ color: "error.main", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Löschen bestätigen
          <IconButton size="small" onClick={() => setShow(false)} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie diese Verbesserung löschen möchten?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShow(false)} color="inherit">
            Nein
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Ja
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={viewDescription !== null}
        onClose={() => setViewDescription(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Beschreibung
          <IconButton size="small" onClick={() => setViewDescription(null)} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {viewDescription}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDescription(null)} color="inherit">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Improvements;
